/**
 * Param Validators is an abstraction of validating params of a function, each validator
 * returns a boolean value indicating whether the validation is passed or not.
 */

import { LIT_ERROR } from '@lit-protocol/constants';

import {
  DecryptFileProps,
  JsonEncryptionRetrieveRequest,
  JsonSaveEncryptionKeyRequest,
  DecryptZipFileWithMetadataProps,
  EncryptFileAndZipWithMetadataProps,
  EncryptToIpfsProps,
  DecryptFromIpfsProps,
  KV,
  ExecuteJsProps,
} from '@lit-protocol/types';

import {
  checkIfAuthSigRequiresChainParam,
  checkType,
  is,
  log,
  throwError,
} from '@lit-protocol/misc';

export const safeParams = ({
  functionName,
  params,
}: {
  functionName: string;
  params: any[] | any;
}) => {
  const validators = paramsValidators as KV;

  const validator = validators[functionName](params);

  if (!validator) {
    log(`This function ${functionName} is skipping params safe guarding.`);
    return true;
  }

  return validator;
};

export const paramsValidators = {
  executeJs: (params: ExecuteJsProps) => {
    // -- prepare params
    const {
      code,
      ipfsId,
      authSig,
      jsParams,
      debug,
      sessionSigs,
      authMethods = [],
    } = params;

    // -- validate: either 'code' or 'ipfsId' must exists
    if (!code && !ipfsId) {
      const message = 'You must pass either code or ipfsId';
      throwError({
        message,
        errorKind: LIT_ERROR.PARAMS_MISSING_ERROR.kind,
        errorCode: LIT_ERROR.PARAMS_MISSING_ERROR.name,
      });
      return false;
    }

    // -- validate: 'code' and 'ipfsId' can't exists at the same time
    if (code && ipfsId) {
      const message = "You cannot have both 'code' and 'ipfs' at the same time";

      throwError({
        message,
        errorKind: LIT_ERROR.INVALID_PARAM_TYPE.kind,
        errorCode: LIT_ERROR.INVALID_PARAM_TYPE.name,
      });
      return false;
    }

    // -- validate: authSig and its type is correct
    if (
      authSig &&
      !checkType({
        value: authSig,
        allowedTypes: ['Object'],
        paramName: 'authSig',
        functionName: 'executeJs',
      })
    )
      return false;

    // -- validate: sessionSigs and its type is correct
    if (sessionSigs && !is(sessionSigs, 'Object', 'sessionSigs', 'executeJs'))
      return false;

    // -- validate: authMethods and its type is correct
    if (
      authMethods &&
      authMethods.length > 0 &&
      !checkType({
        value: authMethods,
        allowedTypes: ['Array'],
        paramName: 'authMethods',
        functionName: 'executeJs',
      })
    )
      return false;

    // -- validate: if sessionSig or authSig exists
    if (!sessionSigs && !authSig) {
      throwError({
        message: 'You must pass either authSig or sessionSigs',
        errorKind: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.kind,
        errorCode: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.name,
      });
      return false;
    }

    // -- validate: if sessionSig and authSig exists
    if (sessionSigs && authSig) {
      throwError({
        message: 'You must pass only one authSig or sessionSigs',
        errorKind: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.kind,
        errorCode: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.name,
      });
      return false;
    }

    return true;
  },

  saveEncryptionKey: (params: JsonSaveEncryptionKeyRequest) => {
    // -- prepare params
    const {
      accessControlConditions,
      evmContractConditions,
      solRpcConditions,
      unifiedAccessControlConditions,
      authSig,
      chain,
      symmetricKey,
      encryptedSymmetricKey,
      permanant,
      permanent,
      sessionSigs,
    } = params;

    if (
      accessControlConditions &&
      !is(
        accessControlConditions,
        'Array',
        'accessControlConditions',
        'saveEncryptionKey'
      )
    )
      return false;
    if (
      evmContractConditions &&
      !is(
        evmContractConditions,
        'Array',
        'evmContractConditions',
        'saveEncryptionKey'
      )
    )
      return false;
    if (
      solRpcConditions &&
      !is(solRpcConditions, 'Array', 'solRpcConditions', 'saveEncryptionKey')
    )
      return false;
    if (
      unifiedAccessControlConditions &&
      !is(
        unifiedAccessControlConditions,
        'Array',
        'unifiedAccessControlConditions',
        'saveEncryptionKey'
      )
    )
      return false;

    // log('authSig:', authSig);
    if (authSig && !is(authSig, 'Object', 'authSig', 'saveEncryptionKey'))
      return false;
    if (
      authSig &&
      !checkIfAuthSigRequiresChainParam(authSig, chain, 'saveEncryptionKey')
    )
      return false;

    if (
      sessionSigs &&
      !is(sessionSigs, 'Object', 'sessionSigs', 'saveEncryptionKey')
    )
      return false;

    if (!sessionSigs && !authSig) {
      throwError({
        message: 'You must pass either authSig or sessionSigs',
        errorKind: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.kind,
        errorCode: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.name,
      });
      return false;
    }

    if (
      symmetricKey &&
      !is(symmetricKey, 'Uint8Array', 'symmetricKey', 'saveEncryptionKey')
    )
      return false;
    if (
      encryptedSymmetricKey &&
      !is(
        encryptedSymmetricKey,
        'Uint8Array',
        'encryptedSymmetricKey',
        'saveEncryptionKey'
      )
    )
      return false;

    // to fix spelling mistake
    if (typeof params.permanant !== 'undefined') {
      params.permanent = params.permanant;
    }

    if (
      (!symmetricKey || symmetricKey == '') &&
      (!encryptedSymmetricKey || encryptedSymmetricKey == '')
    ) {
      throw new Error(
        'symmetricKey and encryptedSymmetricKey are blank.  You must pass one or the other'
      );
    }

    if (
      !accessControlConditions &&
      !evmContractConditions &&
      !solRpcConditions &&
      !unifiedAccessControlConditions
    ) {
      throw new Error(
        'accessControlConditions and evmContractConditions and solRpcConditions and unifiedAccessControlConditions are blank'
      );
    }

    // -- validate: if sessionSig and authSig exists
    if (sessionSigs && authSig) {
      throwError({
        message: 'You must pass only one authSig or sessionSigs',
        errorKind: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.kind,
        errorCode: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.name,
      });
      return false;
    }

    //   -- case: success
    return true;
  },

  getEncryptionKey: (params: JsonEncryptionRetrieveRequest) => {
    const {
      accessControlConditions,
      evmContractConditions,
      solRpcConditions,
      unifiedAccessControlConditions,
      toDecrypt,
      authSig,
      chain,
      sessionSigs,
    } = params;

    // -- validate
    if (
      accessControlConditions &&
      !is(
        accessControlConditions,
        'Array',
        'accessControlConditions',
        'getEncryptionKey'
      )
    )
      return false;

    if (
      evmContractConditions &&
      !is(
        evmContractConditions,
        'Array',
        'evmContractConditions',
        'getEncryptionKey'
      )
    )
      return false;

    if (
      solRpcConditions &&
      !is(solRpcConditions, 'Array', 'solRpcConditions', 'getEncryptionKey')
    )
      return false;

    if (
      unifiedAccessControlConditions &&
      !is(
        unifiedAccessControlConditions,
        'Array',
        'unifiedAccessControlConditions',
        'getEncryptionKey'
      )
    )
      return false;

    log('TYPEOF toDecrypt in getEncryptionKey():', typeof toDecrypt);
    if (!is(toDecrypt, 'String', 'toDecrypt', 'getEncryptionKey')) return false;
    if (authSig && !is(authSig, 'Object', 'authSig', 'getEncryptionKey'))
      return false;
    if (
      sessionSigs &&
      !is(sessionSigs, 'Object', 'sessionSigs', 'getEncryptionKey')
    )
      return false;

    // -- validate: if sessionSig or authSig exists
    if (!sessionSigs && !authSig) {
      throwError({
        message: 'You must pass either authSig or sessionSigs',
        errorKind: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.kind,
        errorCode: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.name,
      });
      return false;
    }

    // -- validate: if sessionSig and authSig exists
    if (sessionSigs && authSig) {
      throwError({
        message: 'You must pass only one authSig or sessionSigs',
        errorKind: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.kind,
        errorCode: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.name,
      });
      return false;
    }

    // -- validate if 'chain' is null
    if (!chain) {
      return false;
    }

    if (
      authSig &&
      !checkIfAuthSigRequiresChainParam(authSig, chain, 'getEncryptionKey')
    )
      return false;

    return true;
  },

  decryptString: (params: any) => {
    const encryptedStringBlob: Blob = params[0];
    const symmKey: Uint8Array = params[1];

    // -- validate
    if (
      !checkType({
        value: encryptedStringBlob,
        allowedTypes: ['Blob', 'File'],
        paramName: 'encryptedStringBlob',
        functionName: 'decryptString',
      })
    )
      return false;

    if (
      !checkType({
        value: symmKey,
        allowedTypes: ['Uint8Array'],
        paramName: 'symmKey',
        functionName: 'decryptString',
      })
    )
      return false;

    // -- success
    return true;
  },

  decryptFile: (params: DecryptFileProps) => {
    // -- validate
    if (
      !checkType({
        value: params.file,
        allowedTypes: ['Blob', 'File'],
        paramName: 'file',
        functionName: 'decryptFile',
      })
    )
      return false;

    // -- validate
    if (
      !checkType({
        value: params.symmetricKey,
        allowedTypes: ['Uint8Array'],
        paramName: 'symmetricKey',
        functionName: 'decryptFile',
      })
    )
      return false;

    return true;
  },

  decryptZipFileWithMetadata: (params: DecryptZipFileWithMetadataProps) => {
    // -- validate
    if (
      params.authSig &&
      !checkType({
        value: params.authSig,
        allowedTypes: ['Object'],
        paramName: 'authSig',
        functionName: 'decryptZipFileWithMetadata',
      })
    )
      return false;

    // -- validate: sessionSigs and its type is correct
    if (
      params.sessionSigs &&
      !is(
        params.sessionSigs,
        'Object',
        'sessionSigs',
        'decryptZipFileWithMetadata'
      )
    )
      return false;

    // -- validate: if sessionSig or authSig exists
    if (!params.sessionSigs && !params.authSig) {
      throwError({
        message: 'You must pass either authSig or sessionSigs',
        errorKind: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.kind,
        errorCode: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.name,
      });
      return false;
    }

    // -- validate: if sessionSig and authSig exists
    if (params.sessionSigs && params.authSig) {
      throwError({
        message: 'You must pass only one authSig or sessionSigs',
        errorKind: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.kind,
        errorCode: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.name,
      });
      return false;
    }

    // -- validate
    if (
      !checkType({
        value: params.file,
        allowedTypes: ['Blob', 'File'],
        paramName: 'file',
        functionName: 'decryptZipFileWithMetadata',
      })
    )
      return false;

    // -- success case
    return true;
  },

  decryptZip: (params: any) => {
    const { encryptedZipBlob, symmKey } = params;

    log('encryptedZipBlob:', encryptedZipBlob);

    // -- validate
    if (
      !checkType({
        value: encryptedZipBlob,
        allowedTypes: ['Blob', 'File'],
        paramName: 'encryptedZipBlob',
        functionName: 'decryptZip',
      })
    )
      return false;

    // -- validate
    if (
      !checkType({
        value: symmKey,
        allowedTypes: ['Uint8Array'],
        paramName: 'symmKey',
        functionName: 'decryptZip',
      })
    )
      return false;

    return true;
  },

  encryptToIpfs: (params: EncryptToIpfsProps) => {
    // -- validate

    log('params:', params);

    if (
      params.authSig &&
      !checkType({
        value: params.authSig,
        allowedTypes: ['Object'],
        paramName: 'authSig',
        functionName: 'encryptToIpfs',
      })
    )
      return false;

    // -- validate
    if (
      params.accessControlConditions &&
      !checkType({
        value: params.accessControlConditions,
        allowedTypes: ['Array'],
        paramName: 'accessControlConditions',
        functionName: 'encryptToIpfs',
      })
    )
      return false;

    // -- validate
    if (
      params.evmContractConditions &&
      !checkType({
        value: params.evmContractConditions,
        allowedTypes: ['Array'],
        paramName: 'evmContractConditions',
        functionName: 'encryptToIpfs',
      })
    )
      return false;

    // -- validate
    if (
      params.solRpcConditions &&
      !checkType({
        value: params.solRpcConditions,
        allowedTypes: ['Array'],
        paramName: 'solRpcConditions',
        functionName: 'encryptToIpfs',
      })
    )
      return false;

    // -- validate
    if (
      params.unifiedAccessControlConditions &&
      !checkType({
        value: params.unifiedAccessControlConditions,
        allowedTypes: ['Array'],
        paramName: 'unifiedAccessControlConditions',
        functionName: 'encryptToIpfs',
      })
    )
      return false;

    // -- validate
    if (
      params.authSig &&
      !checkIfAuthSigRequiresChainParam(
        params.authSig,
        params.chain,
        'encryptToIpfs'
      )
    )
      return false;

    // -- validate: sessionSigs and its type is correct
    if (
      params.sessionSigs &&
      !is(params.sessionSigs, 'Object', 'sessionSigs', 'encryptToIpfs')
    )
      return false;

    // -- validate: if sessionSig or authSig exists
    if (!params.sessionSigs && !params.authSig) {
      throwError({
        message: 'You must pass either authSig or sessionSigs',
        errorKind: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.kind,
        errorCode: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.name,
      });
      return false;
    }

    // -- validate: if sessionSig and authSig exists
    if (params.sessionSigs && params.authSig) {
      throwError({
        message: 'You must pass only one authSig or sessionSigs',
        errorKind: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.kind,
        errorCode: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.name,
      });
      return false;
    }

    // -- validate
    if (
      params.string !== undefined &&
      !checkType({
        value: params.string,
        allowedTypes: ['String'],
        paramName: 'string',
        functionName: 'encryptToIpfs',
      })
    )
      return false;

    // -- validate
    if (
      params.file !== undefined &&
      !checkType({
        value: params.file,
        allowedTypes: ['Blob', 'File'],
        paramName: 'file',
        functionName: 'encryptToIpfs',
      })
    )
      return false;

    // -- success case
    return true;
  },

  decryptFromIpfs: (params: DecryptFromIpfsProps) => {
    // -- validate

    log('params:', params);

    if (
      params.authSig &&
      !checkType({
        value: params.authSig,
        allowedTypes: ['Object'],
        paramName: 'authSig',
        functionName: 'decryptFromIpfs',
      })
    )
      return false;

    // -- validate: sessionSigs and its type is correct
    if (
      params.sessionSigs &&
      !is(params.sessionSigs, 'Object', 'sessionSigs', 'decryptFromIpfs')
    )
      return false;

    // -- validate: if sessionSig or authSig exists
    if (!params.sessionSigs && !params.authSig) {
      throwError({
        message: 'You must pass either authSig or sessionSigs',
        errorKind: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.kind,
        errorCode: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.name,
      });
      return false;
    }

    // -- validate: if sessionSig and authSig exists
    if (params.sessionSigs && params.authSig) {
      throwError({
        message: 'You must pass only one authSig or sessionSigs',
        errorKind: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.kind,
        errorCode: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.name,
      });
      return false;
    }

    // -- success case
    return true;
  },

  encryptFileAndZipWithMetadata: (
    params: EncryptFileAndZipWithMetadataProps
  ) => {
    // -- validate

    log('params:', params);

    if (
      params.authSig &&
      !checkType({
        value: params.authSig,
        allowedTypes: ['Object'],
        paramName: 'authSig',
        functionName: 'encryptFileAndZipWithMetadata',
      })
    )
      return false;

    // -- validate
    if (
      params.accessControlConditions &&
      !checkType({
        value: params.accessControlConditions,
        allowedTypes: ['Array'],
        paramName: 'accessControlConditions',
        functionName: 'encryptFileAndZipWithMetadata',
      })
    )
      return false;

    // -- validate
    if (
      params.evmContractConditions &&
      !checkType({
        value: params.evmContractConditions,
        allowedTypes: ['Array'],
        paramName: 'evmContractConditions',
        functionName: 'encryptFileAndZipWithMetadata',
      })
    )
      return false;

    // -- validate
    if (
      params.solRpcConditions &&
      !checkType({
        value: params.solRpcConditions,
        allowedTypes: ['Array'],
        paramName: 'solRpcConditions',
        functionName: 'encryptFileAndZipWithMetadata',
      })
    )
      return false;

    // -- validate
    if (
      params.unifiedAccessControlConditions &&
      !checkType({
        value: params.unifiedAccessControlConditions,
        allowedTypes: ['Array'],
        paramName: 'unifiedAccessControlConditions',
        functionName: 'encryptFileAndZipWithMetadata',
      })
    )
      return false;

    // -- validate
    if (
      params.authSig &&
      !checkIfAuthSigRequiresChainParam(
        params.authSig,
        params.chain,
        'encryptFileAndZipWithMetadata'
      )
    )
      return false;

    // -- validate: sessionSigs and its type is correct
    if (
      params.sessionSigs &&
      !is(
        params.sessionSigs,
        'Object',
        'sessionSigs',
        'encryptFileAndZipWithMetadata'
      )
    )
      return false;

    // -- validate: if sessionSig or authSig exists
    if (!params.sessionSigs && !params.authSig) {
      throwError({
        message: 'You must pass either authSig or sessionSigs',
        errorKind: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.kind,
        errorCode: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.name,
      });
      return false;
    }

    // -- validate: if sessionSig and authSig exists
    if (params.sessionSigs && params.authSig) {
      throwError({
        message: 'You must pass only one authSig or sessionSigs',
        errorKind: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.kind,
        errorCode: LIT_ERROR.INVALID_ARGUMENT_EXCEPTION.name,
      });
      return false;
    }

    // -- validate
    if (
      !checkType({
        value: params.file,
        allowedTypes: ['File'],
        paramName: 'file',
        functionName: 'encryptFileAndZipWithMetadata',
      })
    )
      return false;

    // -- validate
    if (
      params.readme &&
      !checkType({
        value: params.readme,
        allowedTypes: ['String'],
        paramName: 'readme',
        functionName: 'encryptFileAndZipWithMetadata',
      })
    )
      return false;

    // -- success case
    return true;
  },
};

export type ParamsValidatorsType = typeof paramsValidators;
